import React, { useEffect, useState } from 'react';
import BackButton from "../components/BackButton";
import StreamGaugesService from '../services/StreamGauges';

const FLOOD_COLORS = {
  normal: '#198754', action: '#ffc107', minor: '#fd7e14',
  moderate: '#dc3545', major: '#721c24', unknown: '#6c757d',
};

const CONDITION_COLORS = {
  very_low: '#dc3545', low: '#fd7e14', optimal: '#198754',
  high: '#0d6efd', high_caution: '#6f42c1', blown_out: '#721c24', unknown: '#6c757d',
};

const CONDITION_LABELS = {
  very_low: 'Very Low', low: 'Low', optimal: 'Optimal',
  high: 'High', high_caution: 'Caution', blown_out: 'Blown Out', unknown: 'N/A',
};

const TREND_ARROWS = { rising: '\u2191', falling: '\u2193', stable: '\u2192' };
const TREND_COLORS = { rising: '#0d6efd', falling: '#dc3545', stable: '#6c757d' };

function formatTimestamp(ts) {
  if (!ts) return 'Never';
  var d = new Date(ts);
  return d.toLocaleString('en-US', {
    timeZone: 'America/Denver', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function formatNumber(val) {
  if (val === null || val === undefined) return '\u2014';
  return Number(val).toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function FloodBadge({ status }) {
  var color = FLOOD_COLORS[status] || FLOOD_COLORS.unknown;
  var label = status === 'normal' ? 'Normal' : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span style={{
      backgroundColor: color, color: status === 'action' ? '#000' : '#fff',
      padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 'bold',
    }}>{label}</span>
  );
}

function FishingBadge({ rating }) {
  var color = CONDITION_COLORS[rating] || CONDITION_COLORS.unknown;
  var label = CONDITION_LABELS[rating] || 'N/A';
  return (
    <span style={{
      backgroundColor: color, color: '#fff',
      padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 'bold',
    }}>{label}</span>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{
      backgroundColor: color || '#f8f9fa', borderRadius: '8px',
      padding: '12px 16px', textAlign: 'center', minWidth: '130px',
    }}>
      <div style={{ fontSize: '1.8em', fontWeight: 'bold' }}>{value}</div>
      <div style={{ fontSize: '0.8em', color: '#666' }}>{label}</div>
    </div>
  );
}

// === SVG Hydrograph Component ===
function Hydrograph({ data }) {
  if (!data || !data.current_readings || data.current_readings.length === 0) {
    return (
      <div className="text-center text-muted p-4">
        No readings available for this period. Try syncing data first.
      </div>
    );
  }

  var W = 800, H = 360;
  var pad = { top: 25, right: 30, bottom: 55, left: 75 };
  var cw = W - pad.left - pad.right;
  var ch = H - pad.top - pad.bottom;

  // Parse readings
  var readings = data.current_readings.map(function(r) {
    return { t: new Date(r.datetime).getTime(), v: r.value };
  });

  // Parse forecasts (NOAA)
  var forecasts = (data.forecast || []).map(function(f) {
    return { t: new Date(f.datetime).getTime(), v: f.value };
  });

  // Parse historical forecast (median projection for dates beyond NOAA)
  var histForecast = (data.historical_forecast || []).map(function(f) {
    return { t: new Date(f.datetime).getTime(), v: f.median, p25: f.p25, p75: f.p75 };
  });

  // Parse historical envelope — now uses full datetime from server
  var envelope = (data.historical_envelope || []).map(function(s) {
    return {
      t: new Date(s.datetime).getTime(), median: s.median, mean: s.mean,
      p10: s.p10, p25: s.p25, p75: s.p75, p90: s.p90,
    };
  }).sort(function(a, b) { return a.t - b.t; });

  // Target date marker
  var targetDateTs = data.target_date ? new Date(data.target_date + 'T12:00:00').getTime() : null;

  // Compute time domain
  var allTimes = readings.map(function(r) { return r.t; });
  if (forecasts.length > 0) {
    forecasts.forEach(function(f) { allTimes.push(f.t); });
  }
  if (histForecast.length > 0) {
    histForecast.forEach(function(f) { allTimes.push(f.t); });
  }
  if (targetDateTs) { allTimes.push(targetDateTs); }
  var tMin = Math.min.apply(null, allTimes);
  var tMax = Math.max.apply(null, allTimes);

  // Compute value domain
  var allVals = readings.map(function(r) { return r.v; });
  if (forecasts.length > 0) {
    forecasts.forEach(function(f) { allVals.push(f.v); });
  }
  if (histForecast.length > 0) {
    histForecast.forEach(function(f) { if (f.v) allVals.push(f.v); if (f.p75) allVals.push(f.p75); });
  }
  envelope.forEach(function(s) {
    if (s.p90) allVals.push(s.p90);
    if (s.p10) allVals.push(s.p10);
  });
  var fl = data.flood_levels;
  if (fl) {
    if (fl.action) allVals.push(fl.action);
    if (fl.minor) allVals.push(fl.minor);
  }
  var vMax = Math.max.apply(null, allVals) * 1.1;
  if (vMax < 10) vMax = 10;

  function xScale(t) { return pad.left + ((t - tMin) / (tMax - tMin)) * cw; }
  function yScale(v) { return pad.top + ch - (v / vMax) * ch; }

  // Build polyline strings
  function polylineStr(points) {
    return points.map(function(p) { return xScale(p.t) + ',' + yScale(p.v); }).join(' ');
  }

  // Build polygon for envelope band
  function bandPolygon(statsArr, lowKey, highKey) {
    var forward = [];
    var backward = [];
    statsArr.forEach(function(s) {
      if (s[highKey] != null && s[lowKey] != null) {
        forward.push(xScale(s.t) + ',' + yScale(s[highKey]));
        backward.unshift(xScale(s.t) + ',' + yScale(s[lowKey]));
      }
    });
    if (forward.length < 2) return null;
    return forward.concat(backward).join(' ');
  }

  // Median line
  var medianPoints = envelope.filter(function(s) { return s.median != null; });
  var medianLine = medianPoints.map(function(s) { return xScale(s.t) + ',' + yScale(s.median); }).join(' ');

  // X-axis ticks
  var dayMs = 86400000;
  var totalDays = (tMax - tMin) / dayMs;
  var tickInterval = totalDays <= 8 ? 1 : totalDays <= 16 ? 2 : 5;
  var xTicks = [];
  var tickTime = tMin;
  while (tickTime <= tMax) {
    xTicks.push(tickTime);
    tickTime += tickInterval * dayMs;
  }

  // Y-axis ticks
  var yTicks = [];
  var yStep = Math.pow(10, Math.floor(Math.log10(vMax))) / 2;
  if (vMax / yStep > 8) yStep *= 2;
  if (vMax / yStep < 3) yStep /= 2;
  for (var yv = 0; yv <= vMax; yv += yStep) {
    yTicks.push(yv);
  }

  var p90band = bandPolygon(envelope, 'p10', 'p90');
  var p75band = bandPolygon(envelope, 'p25', 'p75');
  var nowX = xScale(Date.now());

  return (
    <svg viewBox={"0 0 " + W + " " + H} width="100%" style={{ maxHeight: '400px' }}>
      {/* p10-p90 band */}
      {p90band && <polygon points={p90band} fill="#e3f2fd" opacity="0.5" />}
      {/* p25-p75 band */}
      {p75band && <polygon points={p75band} fill="#90caf9" opacity="0.5" />}

      {/* Flood level lines */}
      {fl && fl.major ? <line x1={pad.left} y1={yScale(fl.major)} x2={pad.left + cw} y2={yScale(fl.major)} stroke="#721c24" strokeWidth="1" strokeDasharray="6,3" /> : null}
      {fl && fl.moderate ? <line x1={pad.left} y1={yScale(fl.moderate)} x2={pad.left + cw} y2={yScale(fl.moderate)} stroke="#dc3545" strokeWidth="1" strokeDasharray="6,3" /> : null}
      {fl && fl.minor ? <line x1={pad.left} y1={yScale(fl.minor)} x2={pad.left + cw} y2={yScale(fl.minor)} stroke="#fd7e14" strokeWidth="1" strokeDasharray="6,3" /> : null}
      {fl && fl.action ? <line x1={pad.left} y1={yScale(fl.action)} x2={pad.left + cw} y2={yScale(fl.action)} stroke="#ffc107" strokeWidth="1" strokeDasharray="6,3" /> : null}

      {/* Flood level labels */}
      {fl && fl.major ? <text x={pad.left + cw + 2} y={yScale(fl.major) + 3} fontSize="8" fill="#721c24">Major</text> : null}
      {fl && fl.minor ? <text x={pad.left + cw + 2} y={yScale(fl.minor) + 3} fontSize="8" fill="#fd7e14">Minor</text> : null}

      {/* Historical median line */}
      {medianLine && medianPoints.length > 1 && (
        <polyline points={medianLine} fill="none" stroke="#9e9e9e" strokeWidth="1.5" strokeDasharray="5,5" />
      )}

      {/* Observed readings line */}
      <polyline points={polylineStr(readings)} fill="none" stroke="#1565c0" strokeWidth="2.5" />

      {/* Forecast line (NOAA) */}
      {forecasts.length > 1 && (
        <polyline points={polylineStr(forecasts)} fill="none" stroke="#ff9800" strokeWidth="2" strokeDasharray="8,4" />
      )}

      {/* Historical forecast line (median projection beyond NOAA) */}
      {histForecast.length > 1 && (
        <polyline points={polylineStr(histForecast)} fill="none" stroke="#4caf50" strokeWidth="2" strokeDasharray="6,4" />
      )}

      {/* "Now" vertical line */}
      {nowX > pad.left && nowX < pad.left + cw && (
        <React.Fragment>
          <line x1={nowX} y1={pad.top} x2={nowX} y2={pad.top + ch} stroke="#999" strokeWidth="1" strokeDasharray="3,3" />
          <text x={nowX} y={pad.top - 5} textAnchor="middle" fontSize="9" fill="#999">Now</text>
        </React.Fragment>
      )}

      {/* Target date vertical marker */}
      {targetDateTs && (function() {
        var tx = xScale(targetDateTs);
        if (tx > pad.left && tx < pad.left + cw) {
          var targetLabel = new Date(targetDateTs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          return (
            <React.Fragment>
              <line x1={tx} y1={pad.top} x2={tx} y2={pad.top + ch} stroke="#e91e63" strokeWidth="2" strokeDasharray="4,2" />
              <text x={tx} y={pad.top - 5} textAnchor="middle" fontSize="10" fill="#e91e63" fontWeight="bold">{targetLabel}</text>
            </React.Fragment>
          );
        }
        return null;
      })()}

      {/* X axis */}
      <line x1={pad.left} y1={pad.top + ch} x2={pad.left + cw} y2={pad.top + ch} stroke="#ccc" />
      {xTicks.map(function(t, i) {
        var x = xScale(t);
        var d = new Date(t);
        var label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Denver' });
        return (
          <React.Fragment key={i}>
            <line x1={x} y1={pad.top + ch} x2={x} y2={pad.top + ch + 5} stroke="#999" />
            <text x={x} y={pad.top + ch + 18} textAnchor="middle" fontSize="9" fill="#666">{label}</text>
          </React.Fragment>
        );
      })}

      {/* Y axis */}
      <line x1={pad.left} y1={pad.top} x2={pad.left} y2={pad.top + ch} stroke="#ccc" />
      {yTicks.map(function(v, i) {
        var y = yScale(v);
        return (
          <React.Fragment key={i}>
            <line x1={pad.left - 5} y1={y} x2={pad.left} y2={y} stroke="#999" />
            <line x1={pad.left} y1={y} x2={pad.left + cw} y2={y} stroke="#eee" strokeWidth="0.5" />
            <text x={pad.left - 8} y={y + 3} textAnchor="end" fontSize="9" fill="#666">{formatNumber(v)}</text>
          </React.Fragment>
        );
      })}
      <text x={15} y={pad.top + ch / 2} textAnchor="middle" fontSize="10" fill="#666"
        transform={"rotate(-90, 15, " + (pad.top + ch / 2) + ")"}>Discharge (CFS)</text>

      {/* Legend */}
      <rect x={pad.left + 10} y={pad.top + 5} width={190} height={histForecast.length > 0 ? 84 : forecasts.length > 0 ? 70 : 56} fill="white" opacity="0.85" rx="4" />
      <line x1={pad.left + 15} y1={pad.top + 18} x2={pad.left + 35} y2={pad.top + 18} stroke="#1565c0" strokeWidth="2.5" />
      <text x={pad.left + 40} y={pad.top + 21} fontSize="9" fill="#333">Current Flow</text>
      <line x1={pad.left + 15} y1={pad.top + 32} x2={pad.left + 35} y2={pad.top + 32} stroke="#9e9e9e" strokeWidth="1.5" strokeDasharray="5,5" />
      <text x={pad.left + 40} y={pad.top + 35} fontSize="9" fill="#333">Historical Median</text>
      <rect x={pad.left + 15} y={pad.top + 42} width={20} height={8} fill="#90caf9" opacity="0.6" />
      <text x={pad.left + 40} y={pad.top + 49} fontSize="9" fill="#333">Normal Range (p25-p75)</text>
      {forecasts.length > 0 && (
        <React.Fragment>
          <line x1={pad.left + 15} y1={pad.top + 60} x2={pad.left + 35} y2={pad.top + 60} stroke="#ff9800" strokeWidth="2" strokeDasharray="8,4" />
          <text x={pad.left + 40} y={pad.top + 63} fontSize="9" fill="#333">NOAA Forecast</text>
        </React.Fragment>
      )}
      {histForecast.length > 0 && (
        <React.Fragment>
          <line x1={pad.left + 15} y1={pad.top + (forecasts.length > 0 ? 74 : 60)} x2={pad.left + 35} y2={pad.top + (forecasts.length > 0 ? 74 : 60)} stroke="#4caf50" strokeWidth="2" strokeDasharray="6,4" />
          <text x={pad.left + 40} y={pad.top + (forecasts.length > 0 ? 77 : 63)} fontSize="9" fill="#333">Historical Projection</text>
        </React.Fragment>
      )}
    </svg>
  );
}

var CELL_STYLE = {
  wordWrap: 'break-word', overflowWrap: 'break-word',
  whiteSpace: 'normal', verticalAlign: 'middle', fontSize: '0.85em',
};

var StreamGauges = function() {
  var _useState = function(init) { return useState(init); };
  var _s = function(arr) { return arr; };

  var _gauges = useState([]);
  var gauges = _gauges[0], setGauges = _gauges[1];
  var _totalCount = useState(0);
  var totalCount = _totalCount[0], setTotalCount = _totalCount[1];
  var _forecastCount = useState(0);
  var forecastCount = _forecastCount[0], setForecastCount = _forecastCount[1];
  var _filteredCount = useState(0);
  var filteredCount = _filteredCount[0], setFilteredCount = _filteredCount[1];
  var _lastSiteSync = useState(null);
  var lastSiteSync = _lastSiteSync[0], setLastSiteSync = _lastSiteSync[1];
  var _lastReadingsFetch = useState(null);
  var lastReadingsFetch = _lastReadingsFetch[0], setLastReadingsFetch = _lastReadingsFetch[1];
  var _lastForecastFetch = useState(null);
  var lastForecastFetch = _lastForecastFetch[0], setLastForecastFetch = _lastForecastFetch[1];
  var _filters = useState({ search: '', has_forecast: '', has_river: '' });
  var filters = _filters[0], setFilters = _filters[1];
  var _sortField = useState('station_name');
  var sortField = _sortField[0], setSortField = _sortField[1];
  var _sortDir = useState('asc');
  var sortDir = _sortDir[0], setSortDir = _sortDir[1];
  var _loading = useState(true);
  var loading = _loading[0], setLoading = _loading[1];
  var _syncing = useState(false);
  var syncing = _syncing[0], setSyncing = _syncing[1];
  var _showLog = useState(false);
  var showLog = _showLog[0], setShowLog = _showLog[1];
  var _syncLogs = useState([]);
  var syncLogs = _syncLogs[0], setSyncLogs = _syncLogs[1];
  var _selectedGauge = useState(null);
  var selectedGauge = _selectedGauge[0], setSelectedGauge = _selectedGauge[1];
  var _detailLoading = useState(false);
  var detailLoading = _detailLoading[0], setDetailLoading = _detailLoading[1];
  var _conditions = useState(null);
  var conditions = _conditions[0], setConditions = _conditions[1];
  var _conditionsLoading = useState(false);
  var conditionsLoading = _conditionsLoading[0], setConditionsLoading = _conditionsLoading[1];
  var _graphPeriod = useState('7d');
  var graphPeriod = _graphPeriod[0], setGraphPeriod = _graphPeriod[1];
  var _targetDate = useState('');
  var targetDate = _targetDate[0], setTargetDate = _targetDate[1];

  function loadGauges(date) {
    setLoading(true);
    var f = Object.assign({}, filters);
    if (date) f.date = date;
    StreamGaugesService.getGauges(f).then(function(resp) {
      setGauges(resp.gauges || []);
      setTotalCount(resp.total_count || 0);
      setForecastCount(resp.forecast_count || 0);
      setFilteredCount(resp.filtered_count || 0);
      setLastSiteSync(resp.last_site_sync);
      setLastReadingsFetch(resp.last_readings_fetch);
      setLastForecastFetch(resp.last_forecast_fetch);
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }

  function loadSyncLog() {
    StreamGaugesService.getSyncLog().then(function(resp) {
      setSyncLogs(resp.logs || []);
    });
  }

  function loadConditions(id, period, date) {
    setConditionsLoading(true);
    StreamGaugesService.getConditions(id, period, date || null).then(function(resp) {
      setConditions(resp);
      setConditionsLoading(false);
    }).catch(function() { setConditionsLoading(false); });
  }

  function loadGaugeDetail(id) {
    setDetailLoading(true);
    setConditions(null);
    setGraphPeriod('7d');
    setTargetDate('');
    StreamGaugesService.getGauge(id).then(function(resp) {
      setSelectedGauge(resp.gauge);
      setDetailLoading(false);
      loadConditions(resp.gauge.id, '7d', null);
    }).catch(function() { setDetailLoading(false); });
  }

  function handlePeriodChange(period) {
    setGraphPeriod(period);
    setTargetDate('');
    if (selectedGauge) {
      loadConditions(selectedGauge.id, period, null);
    }
  }

  function handleDateChange(dateStr) {
    setTargetDate(dateStr);
    if (selectedGauge) {
      // Detail view: update graph
      if (dateStr) {
        setGraphPeriod('');
        loadConditions(selectedGauge.id, null, dateStr);
      } else {
        setGraphPeriod('7d');
        loadConditions(selectedGauge.id, null, null);
      }
    } else {
      // Main table view: reload with date
      loadGauges(dateStr || null);
    }
  }

  useEffect(function() { loadGauges(); }, [filters.has_forecast, filters.has_river]);

  function handleSearch() { loadGauges(targetDate || null); }

  function handleSyncAll() {
    setSyncing(true);
    StreamGaugesService.syncAll().then(function() {
      var poll = setInterval(function() {
        StreamGaugesService.getGauges(filters).then(function(resp) {
          if (resp.gauges && resp.gauges.length > 0) {
            setGauges(resp.gauges);
            setTotalCount(resp.total_count || 0);
            setForecastCount(resp.forecast_count || 0);
            setFilteredCount(resp.filtered_count || 0);
            setLastSiteSync(resp.last_site_sync);
            setLastReadingsFetch(resp.last_readings_fetch);
            setLastForecastFetch(resp.last_forecast_fetch);
          }
        });
      }, 15000);
      setTimeout(function() { clearInterval(poll); setSyncing(false); loadGauges(); loadSyncLog(); }, 180000);
    });
  }

  function handleSort(field) {
    if (sortField === field) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  }

  function sortedGauges() {
    return [].concat(gauges).sort(function(a, b) {
      var aVal = a[sortField];
      var bVal = b[sortField];
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  function sortArrow(field) {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  // === DETAIL VIEW ===
  if (selectedGauge) {
    var a = conditions && conditions.assessment ? conditions.assessment : null;
    return (
      <div className="m-5">
        <div className="container-fluid">
          <button className="btn btn-sm btn-outline-secondary mb-3" onClick={function() { setSelectedGauge(null); setConditions(null); }}>
            &larr; Back to All Gauges
          </button>

          {detailLoading ? (
            <div className="text-center p-5">Loading gauge details...</div>
          ) : (
            <React.Fragment>
              <h3>{selectedGauge.station_name}</h3>
              <p className="text-muted">{selectedGauge.gauge_uid} | USGS {selectedGauge.usgs_site_no}
                {selectedGauge.nws_lid ? ' | NWS ' + selectedGauge.nws_lid : ''}
              </p>

              {/* === FISHING CONDITIONS CARD === */}
              {a && a.score != null && (
                <div className="card mb-3" style={{ borderLeft: '5px solid ' + a.color }}>
                  <div className="card-header" style={{ backgroundColor: a.color + '15' }}>
                    <strong>{a.is_prediction ? 'Predicted Conditions for ' + new Date(a.target_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : 'Fishing Conditions'}</strong>
                    {a.is_prediction && a.prediction_source && (
                      <span style={{ fontSize: '0.75em', color: '#666', marginLeft: '10px' }}>
                        (based on {a.prediction_source})
                      </span>
                    )}
                    {a.fishery_type && (
                      <span style={{ fontSize: '0.75em', backgroundColor: a.fishery_type === 'tailwater' ? '#0d6efd' : '#198754', color: '#fff', padding: '2px 8px', borderRadius: '10px', marginLeft: '10px' }}>
                        {a.fishery_type === 'tailwater' ? 'Tailwater' : 'Freestone'}
                      </span>
                    )}
                    {a.priority_tier && (
                      <span style={{ fontSize: '0.75em', backgroundColor: '#ffc107', color: '#000', padding: '2px 8px', borderRadius: '10px', marginLeft: '5px' }}>
                        {'Tier ' + a.priority_tier}
                      </span>
                    )}
                  </div>
                  <div className="card-body">
                    {/* CPW Warning Banner */}
                    {a.cpw_warning && (
                      <div style={{ backgroundColor: '#f8d7da', border: '1px solid #dc3545', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px', color: '#721c24', fontWeight: 'bold', fontSize: '0.9em' }}>
                        {'\u26A0'} {a.cpw_warning}
                      </div>
                    )}
                    <div className="row">
                      <div className="col-md-3 text-center">
                        {/* Star Rating */}
                        <div style={{ fontSize: '1.8em', color: a.color, letterSpacing: '2px' }}>
                          {Array(a.stars || 0).fill('\u2605').join('')}{Array(5 - (a.stars || 0)).fill('\u2606').join('')}
                        </div>
                        <div style={{
                          backgroundColor: a.color, color: '#fff', borderRadius: '12px',
                          padding: '8px 12px', display: 'inline-block', minWidth: '120px', marginTop: '4px',
                        }}>
                          <div style={{ fontSize: '1.3em', fontWeight: 'bold' }}>{a.label}</div>
                          <div style={{ fontSize: '0.8em', opacity: 0.9 }}>Score: {a.score}/100</div>
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#666', marginTop: '6px' }}>{a.description}</div>
                      </div>
                      <div className="col-md-5">
                        <div className="row" style={{ fontSize: '0.85em' }}>
                          <div className="col-4 text-center" style={{ padding: '8px' }}>
                            <div style={{ fontSize: '0.75em', color: '#666' }}>Flow</div>
                            <div style={{ fontSize: '1.6em', fontWeight: 'bold', color: a.flow_score >= 60 ? '#198754' : a.flow_score >= 40 ? '#ffc107' : '#dc3545' }}>{a.flow_score != null ? a.flow_score : '\u2014'}</div>
                          </div>
                          <div className="col-4 text-center" style={{ padding: '8px' }}>
                            <div style={{ fontSize: '0.75em', color: '#666' }}>Temp</div>
                            <div style={{ fontSize: '1.6em', fontWeight: 'bold', color: a.temp_score != null ? (a.temp_score >= 60 ? '#198754' : a.temp_score >= 40 ? '#ffc107' : '#dc3545') : '#999' }}>{a.temp_score != null ? a.temp_score : '\u2014'}</div>
                          </div>
                          <div className="col-4 text-center" style={{ padding: '8px' }}>
                            <div style={{ fontSize: '0.75em', color: '#666' }}>Trend</div>
                            <div style={{ fontSize: '1.6em', fontWeight: 'bold', color: a.trend_score >= 60 ? '#198754' : a.trend_score >= 40 ? '#ffc107' : '#dc3545' }}>{a.trend_score != null ? a.trend_score : '\u2014'}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          {formatNumber(a.current_cfs)} cfs now vs {formatNumber(a.median_cfs)} cfs median
                          {a.percent_of_median ? ' (' + a.percent_of_median + '%)' : ''}
                        </div>
                        {a.water_temp_f && (
                          <div style={{ fontSize: '0.85em', color: '#666' }}>
                            Water temp: {a.water_temp_f}&deg;F
                            {a.water_temp_f >= 55 && a.water_temp_f <= 63 ? ' (prime range)' : a.water_temp_f > 65 ? ' (caution)' : ''}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <div style={{ marginBottom: '12px' }}>
                          <strong>Trend:</strong>{' '}
                          <span style={{ color: TREND_COLORS[a.trend], fontWeight: 'bold', fontSize: '1.1em' }}>
                            {TREND_ARROWS[a.trend] || '\u2014'} {a.trend}
                          </span>
                          <div style={{ fontSize: '0.8em', color: '#555', marginTop: '4px' }}>{a.trend_message}</div>
                        </div>
                        <div>
                          <strong>Season:</strong> <span style={{ fontWeight: 'bold' }}>{a.season}</span>
                          <div style={{ fontSize: '0.8em', color: '#555', marginTop: '4px' }}>{a.season_message}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* === HYDROGRAPH CARD === */}
              <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <strong>Hydrograph</strong>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span style={{ fontSize: '0.8em', color: '#666' }}>History:</span>
                    {['7d', '14d', '30d'].map(function(p) {
                      return (
                        <button key={p} className={'btn btn-sm me-1 ' + (graphPeriod === p && !targetDate ? 'btn-primary' : 'btn-outline-secondary')}
                          onClick={function() { handlePeriodChange(p); }}
                          disabled={conditionsLoading}>
                          {p}
                        </button>
                      );
                    })}
                    <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '8px' }}>Trip Date:</span>
                    <input type="date" className="form-control form-control-sm" style={{ width: '150px' }}
                      value={targetDate}
                      min={new Date().toISOString().split('T')[0]}
                      max={new Date(Date.now() + 60 * 86400000).toISOString().split('T')[0]}
                      onChange={function(e) { handleDateChange(e.target.value); }}
                      disabled={conditionsLoading}
                    />
                    {targetDate && (
                      <button className="btn btn-sm btn-outline-danger" onClick={function() { handleDateChange(''); }}>Clear</button>
                    )}
                  </div>
                </div>
                <div className="card-body" style={{ overflowX: 'auto', padding: '12px' }}>
                  {conditionsLoading ? (
                    <div className="text-center text-muted p-4">Loading graph data...</div>
                  ) : conditions ? (
                    <Hydrograph data={conditions} />
                  ) : (
                    <div className="text-center text-muted p-4">No conditions data available.</div>
                  )}
                </div>
              </div>

              {/* Current Conditions Card */}
              <div className="card mb-3">
                <div className="card-header"><strong>Current Conditions</strong></div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-2">
                      <strong>Discharge:</strong><br />
                      <span style={{ fontSize: '1.4em', fontWeight: 'bold' }}>
                        {formatNumber(selectedGauge.current_discharge_cfs)} cfs
                      </span>
                    </div>
                    <div className="col-md-2">
                      <strong>Gage Height:</strong><br />
                      <span style={{ fontSize: '1.4em', fontWeight: 'bold' }}>
                        {formatNumber(selectedGauge.current_gage_height_ft)} ft
                      </span>
                    </div>
                    <div className="col-md-2">
                      <strong>Water Temp:</strong><br />
                      {selectedGauge.water_temp_f ? (
                        <span style={{ fontSize: '1.4em', fontWeight: 'bold', color: selectedGauge.water_temp_f > 68 ? '#dc3545' : selectedGauge.water_temp_f > 55 ? '#fd7e14' : selectedGauge.water_temp_f > 39 ? '#198754' : '#0d6efd' }}>
                          {selectedGauge.water_temp_f}&deg;F
                        </span>
                      ) : (
                        <span style={{ fontSize: '1.4em', color: '#999' }}>{'\u2014'}</span>
                      )}
                      {selectedGauge.water_temp_f && (
                        <div style={{ fontSize: '0.7em', color: '#666' }}>
                          {selectedGauge.water_temp_f > 68 ? 'Stressed trout' : selectedGauge.water_temp_f > 55 ? 'Active feeding' : selectedGauge.water_temp_f > 45 ? 'Good for trout' : selectedGauge.water_temp_f > 39 ? 'Slow fishing' : 'Very cold'}
                        </div>
                      )}
                    </div>
                    <div className="col-md-2">
                      <strong>Turbidity:</strong><br />
                      {selectedGauge.turbidity_ntu ? (
                        <span style={{ fontSize: '1.4em', fontWeight: 'bold', color: selectedGauge.turbidity_ntu > 50 ? '#dc3545' : selectedGauge.turbidity_ntu > 20 ? '#fd7e14' : '#198754' }}>
                          {formatNumber(selectedGauge.turbidity_ntu)} NTU
                        </span>
                      ) : (
                        <span style={{ fontSize: '1.4em', color: '#999' }}>{'\u2014'}</span>
                      )}
                      {selectedGauge.turbidity_ntu && (
                        <div style={{ fontSize: '0.7em', color: '#666' }}>
                          {selectedGauge.turbidity_ntu > 50 ? 'Muddy - poor visibility' : selectedGauge.turbidity_ntu > 20 ? 'Stained - use bright flies' : selectedGauge.turbidity_ntu > 5 ? 'Slightly off-color' : 'Clear water'}
                        </div>
                      )}
                    </div>
                    <div className="col-md-2">
                      <strong>Trend:</strong><br />
                      <span style={{ fontSize: '1.4em', color: TREND_COLORS[selectedGauge.trend] }}>
                        {TREND_ARROWS[selectedGauge.trend] || '\u2014'} {selectedGauge.trend}
                      </span>
                    </div>
                    <div className="col-md-2">
                      <strong>Last Reading:</strong><br />
                      <span style={{ fontSize: '0.85em' }}>{formatTimestamp(selectedGauge.current_reading_at)}</span>
                      {selectedGauge.quality_flag && (
                        <div style={{ fontSize: '0.75em', color: '#999' }}>
                          {selectedGauge.quality_flag === 'P' ? 'Provisional' : selectedGauge.quality_flag === 'A' ? 'Approved' : selectedGauge.quality_flag}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="card mb-3">
                <div className="card-header"><strong>Gauge Metadata</strong></div>
                <div className="card-body">
                  <div className="row" style={{ fontSize: '0.9em' }}>
                    <div className="col-md-4">
                      <p><strong>River Name:</strong> {selectedGauge.river_name || '\u2014'}</p>
                      <p><strong>Elevation:</strong> {selectedGauge.elevation_ft ? formatNumber(selectedGauge.elevation_ft) + ' ft' : '\u2014'}</p>
                      <p><strong>Drainage Area:</strong> {selectedGauge.drainage_area_sqmi ? formatNumber(selectedGauge.drainage_area_sqmi) + ' sq mi' : '\u2014'}</p>
                    </div>
                    <div className="col-md-4">
                      <p><strong>HUC Code:</strong> {selectedGauge.huc_code || '\u2014'}</p>
                      <p><strong>County:</strong> {selectedGauge.county || '\u2014'}</p>
                      <p><strong>Has Forecast:</strong> {selectedGauge.has_forecast ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="col-md-4">
                      <p><strong>Latitude:</strong> {selectedGauge.latitude}</p>
                      <p><strong>Longitude:</strong> {selectedGauge.longitude}</p>
                      <p><strong>gauge_uid:</strong> <code>{selectedGauge.gauge_uid}</code></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* External Links */}
              <div className="card mb-3">
                <div className="card-body">
                  {selectedGauge.usgs_url && (
                    <a href={selectedGauge.usgs_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary me-2">
                      View on USGS
                    </a>
                  )}
                  {selectedGauge.noaa_url && (
                    <a href={selectedGauge.noaa_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-info me-2">
                      View on NOAA
                    </a>
                  )}
                </div>
              </div>
            </React.Fragment>
          )}

          <div className="text-center mt-4" style={{ fontSize: '0.75em', color: '#999' }}>
            Data: USGS National Water Information System | Forecasts: NOAA National Water Prediction Service
          </div>
        </div>
      </div>
    );
  }

  // === MAIN TABLE VIEW ===
  return (
    <div className="m-5">
      <div className="container-fluid">
        <BackButton />
        <h2 className="text-center">Colorado Stream Gauges</h2>
        <p className="text-center text-muted" style={{ fontSize: '0.85em' }}>
          Real-time USGS stream gauge data for all active gauges in Colorado. Forecasts from NOAA NWPS.
        </p>

        {/* Summary Cards */}
        <div className="d-flex justify-content-center gap-3 mb-3">
          <SummaryCard label="Total Active Gauges" value={totalCount} />
          <SummaryCard label="With Forecasts" value={forecastCount} color="#d4edda" />
          <SummaryCard label="Showing" value={filteredCount} color="#cfe2ff" />
        </div>

        {/* Sync Info + Action Buttons */}
        <div className="row mb-3 align-items-center">
          <div className="col-md-6">
            <div style={{ fontSize: '0.8em', color: '#666' }}>
              <div>Last site sync: {formatTimestamp(lastSiteSync)}</div>
              <div>Last readings: {formatTimestamp(lastReadingsFetch)}</div>
              <div>Last forecasts: {formatTimestamp(lastForecastFetch)}</div>
            </div>
          </div>
          <div className="col-md-6 text-end">
            <button className="btn btn-sm btn-success me-1" onClick={handleSyncAll} disabled={syncing}>
              {syncing ? 'Syncing... (this takes a minute)' : 'Sync All Data'}
            </button>
            <button className="btn btn-sm btn-outline-secondary me-1" onClick={function() { setShowLog(!showLog); if (!showLog) loadSyncLog(); }}>
              {showLog ? 'Hide Log' : 'Sync Log'}
            </button>
            <a href="/stream_gauges/export_csv" className="btn btn-sm btn-outline-dark me-1" target="_blank">CSV</a>
            <a href="/stream_gauges/export_geojson" className="btn btn-sm btn-outline-dark" target="_blank">GeoJSON</a>
          </div>
        </div>

        {/* Sync Log */}
        {showLog && (
          <div className="card mb-3">
            <div className="card-header"><strong>Sync History</strong></div>
            <div className="card-body" style={{ maxHeight: '250px', overflowY: 'auto' }}>
              <table className="table table-sm table-bordered mb-0">
                <thead><tr><th>Job</th><th>Time</th><th>Processed</th><th>Created</th><th>Updated</th><th>Status</th><th>Duration</th></tr></thead>
                <tbody>
                  {syncLogs.map(function(log, i) {
                    return (
                      <tr key={i}>
                        <td style={{ fontSize: '0.8em' }}>{log.job_name}</td>
                        <td style={{ fontSize: '0.8em' }}>{formatTimestamp(log.started_at)}</td>
                        <td>{log.records_processed}</td>
                        <td>{log.records_created}</td>
                        <td>{log.records_updated}</td>
                        <td>
                          <span style={{
                            color: log.status === 'success' ? '#198754' : log.status === 'partial_failure' ? '#fd7e14' : '#dc3545',
                            fontWeight: 'bold', fontSize: '0.85em',
                          }}>
                            {log.status === 'success' ? 'OK' : log.status === 'partial_failure' ? 'PARTIAL' : 'FAIL'}
                          </span>
                          {log.error_message && <div style={{ fontSize: '0.7em', color: '#dc3545' }}>{log.error_message.substring(0, 100)}</div>}
                        </td>
                        <td style={{ fontSize: '0.8em' }}>{log.duration_ms ? (log.duration_ms / 1000).toFixed(1) + 's' : '\u2014'}</td>
                      </tr>
                    );
                  })}
                  {syncLogs.length === 0 && <tr><td colSpan="7" className="text-muted text-center">No sync runs yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filters + Date Picker */}
        <div className="row mb-3 g-2">
          <div className="col-md-3">
            <div className="input-group input-group-sm">
              <input type="text" className="form-control" placeholder="Search station, site #, river..."
                value={filters.search}
                onChange={function(e) { setFilters(Object.assign({}, filters, { search: e.target.value })); }}
                onKeyDown={function(e) { if (e.key === 'Enter') handleSearch(); }}
              />
              <button className="btn btn-outline-secondary" onClick={handleSearch}>Search</button>
            </div>
          </div>
          <div className="col-md-3">
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={{ fontSize: '0.85em' }}>Date</span>
              <input type="date" className="form-control"
                value={targetDate}
                min={new Date().toISOString().split('T')[0]}
                max={new Date(Date.now() + 60 * 86400000).toISOString().split('T')[0]}
                onChange={function(e) { handleDateChange(e.target.value); }}
              />
              {targetDate && (
                <button className="btn btn-outline-danger" onClick={function() { handleDateChange(''); }}>Today</button>
              )}
            </div>
          </div>
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.has_forecast}
              onChange={function(e) { setFilters(Object.assign({}, filters, { has_forecast: e.target.value })); }}>
              <option value="">All Gauges</option>
              <option value="true">With Forecast</option>
            </select>
          </div>
          <div className="col-md-4">
            <span className="text-muted" style={{ fontSize: '0.85em', lineHeight: '31px' }}>
              {loading ? 'Loading...' : 'Showing ' + gauges.length + ' of ' + totalCount + ' gauges'}
              {targetDate && (' \u2014 Forecast for ' + new Date(targetDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' }))}
            </span>
          </div>
        </div>

        {/* Empty state */}
        {!loading && gauges.length === 0 && (
          <div className="alert alert-info text-center">
            No stream gauge data yet. Click <strong>Sync All Data</strong> to pull all Colorado USGS gauges and current conditions.
          </div>
        )}

        {/* Table */}
        {gauges.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-hover table-bordered table-striped table-sm">
              <thead className="table-secondary">
                <tr>
                  <th style={Object.assign({}, CELL_STYLE, { cursor: 'pointer', width: '50px', textAlign: 'center' })} onClick={function() { handleSort('score'); }}>
                    Score{sortArrow('score')}
                  </th>
                  <th style={Object.assign({}, CELL_STYLE, { cursor: 'pointer', width: '200px' })} onClick={function() { handleSort('station_name'); }}>
                    Station{sortArrow('station_name')}
                  </th>
                  <th style={Object.assign({}, CELL_STYLE, { cursor: 'pointer', width: '110px' })} onClick={function() { handleSort('river_name'); }}>
                    River{sortArrow('river_name')}
                  </th>
                  <th style={Object.assign({}, CELL_STYLE, { width: '60px', textAlign: 'center' })}>Type</th>
                  <th style={Object.assign({}, CELL_STYLE, { cursor: 'pointer', width: '80px', textAlign: 'right' })} onClick={function() { handleSort('current_discharge_cfs'); }}>
                    CFS{sortArrow('current_discharge_cfs')}
                  </th>
                  <th style={Object.assign({}, CELL_STYLE, { width: '55px', textAlign: 'center' })}>% Med</th>
                  <th style={Object.assign({}, CELL_STYLE, { width: '55px', textAlign: 'center' })}>Temp</th>
                  <th style={Object.assign({}, CELL_STYLE, { width: '40px', textAlign: 'center' })}>Trnd</th>
                  <th style={Object.assign({}, CELL_STYLE, { width: '80px', textAlign: 'center' })}>Condition</th>
                  <th style={Object.assign({}, CELL_STYLE, { width: '100px', fontFamily: 'monospace', fontSize: '0.7em' })}>Gauge UID</th>
                </tr>
              </thead>
              <tbody>
                {sortedGauges().map(function(g) {
                  var starStr = '';
                  for (var si = 0; si < (g.stars || 0); si++) starStr += '\u2605';
                  for (var sj = (g.stars || 0); sj < 5; sj++) starStr += '\u2606';
                  return (
                    <tr key={g.id} style={{ cursor: 'pointer' }} onClick={function() { loadGaugeDetail(g.id); }}>
                      <td style={Object.assign({}, CELL_STYLE, { textAlign: 'center' })}>
                        <span style={{ color: g.star_color || '#999', fontWeight: 'bold' }}>{g.score != null ? g.score : '\u2014'}</span>
                        <div style={{ fontSize: '0.7em', color: g.star_color || '#999', letterSpacing: '1px' }}>{starStr}</div>
                      </td>
                      <td style={CELL_STYLE}>
                        {g.station_name}
                        {g.priority_tier && (
                          <span style={{ fontSize: '0.65em', backgroundColor: '#ffc107', color: '#000', padding: '1px 4px', borderRadius: '6px', marginLeft: '4px' }}>{'T' + g.priority_tier}</span>
                        )}
                      </td>
                      <td style={CELL_STYLE}>{g.river_name}</td>
                      <td style={Object.assign({}, CELL_STYLE, { textAlign: 'center', fontSize: '0.7em' })}>
                        {g.fishery_type ? (
                          <span style={{ backgroundColor: g.fishery_type === 'tailwater' ? '#0d6efd' : '#198754', color: '#fff', padding: '1px 5px', borderRadius: '6px' }}>
                            {g.fishery_type === 'tailwater' ? 'TW' : 'FS'}
                          </span>
                        ) : '\u2014'}
                      </td>
                      <td style={Object.assign({}, CELL_STYLE, { textAlign: 'right', fontWeight: 'bold' })}>
                        {formatNumber(g.current_discharge_cfs)}
                      </td>
                      <td style={Object.assign({}, CELL_STYLE, { textAlign: 'center', fontSize: '0.8em' })}>
                        {g.percent_of_median ? g.percent_of_median + '%' : '\u2014'}
                      </td>
                      <td style={Object.assign({}, CELL_STYLE, { textAlign: 'center' })}>
                        {g.water_temp_f ? (
                          <span style={{ color: g.water_temp_f > 67 ? '#dc3545' : g.water_temp_f > 55 ? '#fd7e14' : g.water_temp_f > 39 ? '#198754' : '#0d6efd', fontWeight: 'bold', fontSize: '0.85em' }}>
                            {g.water_temp_f + '\u00B0'}
                          </span>
                        ) : '\u2014'}
                      </td>
                      <td style={Object.assign({}, CELL_STYLE, { textAlign: 'center' })}>
                        <span style={{ color: TREND_COLORS[g.trend], fontWeight: 'bold', fontSize: '1.1em' }}>
                          {TREND_ARROWS[g.trend] || '\u2014'}
                        </span>
                      </td>
                      <td style={Object.assign({}, CELL_STYLE, { textAlign: 'center' })}>
                        <FishingBadge rating={g.fishing_condition} />
                      </td>
                      <td style={Object.assign({}, CELL_STYLE, { fontFamily: 'monospace', fontSize: '0.65em', color: '#666' })}>{g.gauge_uid}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Attribution */}
        <div className="text-center mt-4" style={{ fontSize: '0.75em', color: '#999' }}>
          Data: USGS National Water Information System | Forecasts: NOAA National Water Prediction Service
        </div>
      </div>
    </div>
  );
};

export default StreamGauges;
